import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, ɵstringify, forwardRef, NgZone, InjectFlags, Directive, Inject, EventEmitter, Output, HostListener, Input, ElementRef, HostBinding, PLATFORM_ID, Renderer2, Injector, TemplateRef, ViewContainerRef, NgModule, Pipe, Optional } from '@angular/core';
import * as i1 from '@angular/router';
import { take, map, catchError, retryWhen, mergeMap, filter, exhaustMap, first, switchMap, startWith, scan, shareReplay, throttleTime, distinctUntilChanged, tap } from 'rxjs/operators';
import * as i3 from 'rxjs';
import { Observable, throwError, timer, BehaviorSubject, of, merge, asapScheduler, fromEventPattern, Subject, combineLatest, fromEvent } from 'rxjs';
import { toByteArray } from 'base64-js';
import { mapValues, cloneDeep, isBoolean, find, get, has } from 'lodash-es';
import * as i1$4 from '@angular/forms';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1$1 from '@angular/common/http';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import * as Sentry from '@sentry/browser';
import { ToastrService } from 'ngx-toastr';
import * as i1$3 from '@angular/common';
import { DOCUMENT, CurrencyPipe, isPlatformServer, CommonModule } from '@angular/common';
import { WINDOW, ANIMATION_FRAME, PERFORMANCE } from '@ng-web-apis/common';
import * as i1$2 from 'ngx-simple-modal';
import { Jsona } from 'jsona';
import { __decorate, __metadata } from 'tslib';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import momentMini from 'moment-mini';
import { parsePhoneNumberFromString, AsYouType, isValidNumber } from 'libphonenumber-js';
import * as i1$5 from '@angular/platform-browser';

function fadeIn(duration) {
    const options = { duration: duration || 200 };
    return trigger('fadeIn', [
        state('in', style({ opacity: 1 })),
        transition('void => *', [
            style({ opacity: 0 }),
            animate(options.duration, style({ opacity: 1 })),
        ]),
    ]);
}
function fadeInOut(duration, inTimingFunction, outTimingFunction) {
    const options = {
        duration: duration || '0.3s',
        inTimingFunction: inTimingFunction || 'ease-in',
        outTimingFunction: outTimingFunction || 'ease-out',
    };
    return trigger('fadeInOut', [
        state('*', style({ opacity: 1 })),
        transition(':enter', [
            style({ opacity: 0 }),
            animate(`${options.duration} ${options.inTimingFunction}`),
        ]),
        transition(':leave', [
            animate(`${options.duration} ${options.outTimingFunction}`, style({ opacity: 0 })),
        ]),
    ]);
}
function fadeOut(duration, inTimingFunction, outTimingFunction) {
    const options = {
        duration: duration || '0.3s',
        inTimingFunction: inTimingFunction || 'ease-in',
        outTimingFunction: outTimingFunction || 'ease-out',
    };
    return trigger('fadeOut', [
        state('*', style({ opacity: 1 })),
        transition(':enter', [
            style({ opacity: 0 }),
            animate(`${options.duration} ${options.inTimingFunction}`),
        ]),
        transition(':leave', [
            animate(`${options.duration} ${options.outTimingFunction}`, style({
                opacity: 0,
                height: 0,
                marginBottom: 0,
            })),
        ]),
    ]);
}

const DEFAULT_MOMENT_DATE_FORMAT = 'MMMM D, YYYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';

const ERROR = { SOMETHING_WENT_WRONG: 'Something went wrong' };

// Configuration objects
const SENTINEL_CONFIG = new InjectionToken('SENTINEL_CONFIG');
const API_INTECEPTOR_CONFIG = new InjectionToken('API_INTECEPTOR_CONFIG');
const AUTH_TOKEN_CONFIG = new InjectionToken('AUTH_TOKEN_CONFIG');
const ROUTE_EXTRAS_STATE_GUARD_CONFIG = new InjectionToken('ROUTE_EXTRAS_STATE_GUARD_CONFIG');
// Providers
const USER_SERVICE_TOKEN = new InjectionToken('USER_SERVICE_TOKEN');
const GLOBAL_ERROR_STATUS_HANDLER_TOKEN = new InjectionToken('GLOBAL_ERROR_STATUS_HANDLER_TOKEN');

const WORD_WITH_SPECIAL_CHARS_GLOBAL = /[A-Za-z$&+,:;=?@#|'<>^*()%!]/g;
const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(\[?(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\]?|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const FACEBOOK_CAMP_ID = /^[0-9]{17}$/;
const FACEBOOK_USERNAME_PATTERN = /[^a-zA-Z0-9.]/g;
const BATH_PATTERN = {
    0: { pattern: /[0-9]/ },
    5: { pattern: /[0|5]/ },
};
const LENDER_MARGIN_PATTERN = /^(\+|-)?[0-9]+([.,][0-9]{0,3})?$/;
const GOOGLE_ANALYTICS_TRACKING_ID_PATTERN = /UA-\d+-\d+/;
const FACEBOOK_TRACKING_ID_PATTERN = /^\d+$/;
const GOOGLE_ADWORDS_PATTERN = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
const GOOGLE_TAG_MANAGER_PATTERN = /GTM-[a-zA-Z0-9]{1,}$/;
const LENDING_PAD_ID = /^[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}$/;

const COMMON_TOAST = {
    SAVED: 'Saved',
    REMOVED: 'Removed',
    SUCCESS: 'Success',
    DONE: 'Done',
    CHANGES_SAVED: 'Changes saved',
    COPIED_TO_CLIPBOARD: 'Copied to clipboard',
};

const TRACKING_PROVIDER = {
    GA: 'google_analytics',
    GTM: 'google_tag_manager',
    FB: 'facebook_pixel',
};

const WARNING = { NO_NETWORK: 'No Internet Connection' };

class ExitGuard {
    canDeactivate(component) {
        return component.canDeactivate ? component.canDeactivate() : true;
    }
}
ExitGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExitGuard, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ExitGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExitGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExitGuard, decorators: [{
            type: Injectable
        }] });

function assertTemplate(property, templateRef) {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
        throw new Error(`${property} must be a TemplateRef, but received '${ɵstringify(templateRef)}'.`);
    }
}

function b64toBlob(base64, mime) {
    const data = base64.split(';');
    const type = mime || data[0].split(':')[1];
    return new Observable((observer) => {
        const byteArray = toByteArray(data[1].split(',')[1]);
        const blob = new Blob([byteArray], { type });
        observer.next(blob);
        observer.complete();
    });
}

/**
 *  Lodash's chain() for lodash-es
 *  https://github.com/lodash/lodash/issues/3298
 *
 */
function chain(input, chainableFunctions) {
    let value = input;
    const chainables = chainableFunctions;
    const wrapper = {
        ...mapValues(chainables, (f) => (...args) => {
            value = f(value, ...args);
            return wrapper;
        }),
        value: () => value,
    };
    return wrapper;
}

/**
 * Returns response with formatted User Fees and Lead Fees
 *
 */
function remapCommissions(data) {
    if (!data) {
        return null;
    }
    return {
        ...cloneDeep(data),
        subtotal: data.commissions.reduce((previous, current) => previous + current.value, 0),
    };
}
function remapLeadFees(item) {
    const subtotal = {
        other: 0,
        buyer: 0,
        seller: 0,
    };
    item.fees.forEach((subItem) => {
        if (subItem.other_amount) {
            subtotal.other += +subItem.other_amount;
        }
        if (subItem.buyer_amount) {
            subtotal.buyer += +subItem.buyer_amount;
        }
        if (subItem.seller_amount) {
            subtotal.seller += +subItem.seller_amount;
        }
    });
    return { ...item, subtotal };
}
function formatFees(lead) {
    return {
        ...lead,
        selling: {
            ...lead.selling,
            commissions: remapCommissions(lead.selling.commissions),
            fees: lead.selling?.fees?.map(remapLeadFees) || [],
        },
        refinance: {
            ...lead.refinance,
            ...lead.cash_out,
            commissions: remapCommissions(lead.refinance?.commissions || lead.cash_out?.commissions),
            fees: lead.refinance?.fees?.map(remapLeadFees) || lead.cash_out?.fees?.map(remapLeadFees) || [],
        },
    };
}

/**
 *  Display address concatenation with apartment
 *
 */
function formatAddressWithUnit(address, unit) {
    if (!address) {
        return '';
    }
    if (!unit) {
        return address;
    }
    const template = address.split(',');
    return `${template[0]}, ${`${unit}`},${template[1]},${template[2]}`;
}

function upperFirst(word) {
    return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
}

/**
 * Transforms lodash_case string Title Case
 *
 */
function lodashToTitle(param) {
    if (!param) {
        return param;
    }
    return param
        .toLowerCase()
        .split(' ')
        .join('')
        .split('_')
        .map(upperFirst)
        .filter(String)
        .join(' ');
}

function formatApiErrors(response) {
    return response.map((item) => {
        const pointer = item.source.pointer.split('/').reverse()[0];
        if (!pointer || pointer === 'base') {
            return item.detail;
        }
        return `${lodashToTitle(pointer)}: ${item.detail}`;
    });
}

/**
 * Format Email Template Shortcodes
 *
 */
function formatEmailShortCodes(shortcodes) {
    return shortcodes.map((item, index) => {
        if (index === shortcodes.length - 1) {
            return `%${item}%`;
        }
        return `%${item}%, `;
    });
}

/**
 * Extract file name from Content-Disposition header
 *
 */
function getFileName(headers) {
    return headers.get('Content-Disposition').split('filename=')[1].replace(/"/gi, '');
}

/**
 * Separates user name to get initials.
 *
 * @return {string}
 */
function getInitials(fullName) {
    if (!fullName) {
        return '';
    }
    const initials = fullName.match(/\b\w/g) || [];
    return ((initials.shift() || '') + (initials.shift() || '')).toUpperCase();
}

function remapGooglePlacesAddress(data, fullAddress = '') {
    // street_number                -> address street number
    // route                        -> the street name
    // locality                     -> the city / town
    // administrative_area_level_3  -> the city / town
    // neighborhood                 -> the city / town
    // administrative_area_level_1  -> the state
    // postal_code                  -> zip / postal code
    // https://developers.google.com/maps/documentation/geocoding/overview?csw=1#JSON
    const address = {};
    let formattedCityName;
    data.forEach((item) => {
        address[item.types[0]] = {
            long_name: item.long_name,
            short_name: item.short_name,
        };
    });
    if (fullAddress) {
        const localityLongName = address?.locality?.long_name || '';
        const localityShortName = address?.locality?.short_name || '';
        const administrativeAreaShortName = address?.administrative_area_level_3?.short_name || '';
        const administrativeAreaLongName = address?.administrative_area_level_3?.long_name || '';
        const neighborhoodLongName = address?.neighborhood?.long_name || '';
        const neighborhoodShortName = address?.neighborhood?.short_name || '';
        switch (true) {
            // this is a solution for bug related to NY
            case (address?.administrative_area_level_1?.long_name === 'New York'):
                formattedCityName = address?.sublocality_level_1?.long_name;
                break;
            case (fullAddress.includes(localityLongName)):
                formattedCityName = localityLongName;
                break;
            case (fullAddress.includes(localityShortName)):
                formattedCityName = localityShortName;
                break;
            case (fullAddress.includes(administrativeAreaLongName)):
                formattedCityName = administrativeAreaLongName;
                break;
            case (fullAddress.includes(administrativeAreaShortName)):
                formattedCityName = administrativeAreaShortName;
                break;
            case (fullAddress.includes(neighborhoodLongName)):
                formattedCityName = neighborhoodLongName;
                break;
            case (fullAddress.includes(neighborhoodShortName)):
                formattedCityName = neighborhoodShortName;
                break;
            // Locality is default city/town name.
            default:
                formattedCityName = localityLongName;
        }
    }
    return {
        street: [address?.street_number?.long_name, address?.route?.long_name].filter(Boolean).join(' '),
        city: formattedCityName || address?.locality?.long_name || address?.administrative_area_level_3?.long_name || address?.neighborhood?.long_name || '',
        state: address?.administrative_area_level_1?.short_name || '',
        zip: address?.postal_code?.long_name || '',
        country: address?.country?.long_name || '',
    };
}

/**
 * Returns whether or not user permitted to do action.
 *
 * @return {boolean}
 */
function isPermitted(permittedSubject, permittedActions) {
    if (!permittedSubject) {
        return false;
    }
    if (Array.isArray(permittedActions)) {
        return permittedActions.some((permittedAction) => !!~permittedSubject.indexOf(permittedAction));
    }
    return !!~permittedSubject.indexOf(permittedActions);
}

/**
 * Returns whether or not the ROUTE contains substring or ANY string from array.
 *
 * @return {boolean}
 */
function isUrlIncludes(params, router) {
    if (!params) {
        return false;
    }
    if (router.url === '/') {
        return true;
    }
    const urlPartials = router.url.split('?')[0].split('/');
    if (Array.isArray(params)) {
        return params.some((param) => !!~urlPartials.indexOf(param));
    }
    return !!~urlPartials.indexOf(params);
}

/**
 * Round number to precision
 *
 */
function round(value, decimals) {
    return Number(`${Math.round(+`${value}e${decimals}`)}e-${decimals}`);
}

/**
 * Calculate percentage of two numbers
 *
 */
function percent(obtained, total, decimals) {
    if (!total) {
        return 0;
    }
    return round((obtained * 100) / total, decimals);
}

/**
 * Returns config for common forward refs
 *
 */
function provideRefs(component) {
    return [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => component),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => component),
            multi: true,
        },
    ];
}

/**
 * Rounds a number up to quarter
 *
 */
function roundQuarter(value) {
    if (!value) {
        return 0;
    }
    const balance = value % 0.25;
    return !balance ? value : value - +(balance - 0.25).toFixed(2);
}

/**
 * Transform string into the number
 *
 */
function stringToNumber(value) {
    if (typeof value === 'number') {
        return value;
    }
    if (value !== null && value !== undefined) {
        const result = parseFloat(value.replace(WORD_WITH_SPECIAL_CHARS_GLOBAL, ''));
        if (!Number.isNaN(Number(result))) {
            return result;
        }
    }
    return 0;
}

/**
 * Returns dashed string
 *
 * @return {string}
 */
function toDashCase(name) {
    return name ? name.replace(/\s+/g, '-').toLowerCase() : null;
}

/**
 * Convert form value object to FormData object
 *
 */
function toFormData(value) {
    const result = new FormData();
    Object.keys(value).forEach((key) => {
        if (!!value[key] || isBoolean(value[key])) {
            result.append(key, value[key]);
        }
    });
    return result;
}

/**
 * Convert object to query params string
 *
 */
function toQueryParams(params) {
    return Object.keys(params).map((key) => `${key}=${encodeURIComponent(params[key] || '')}`).join('&');
}

function toTitleCase(words) {
    if (!words) {
        return '';
    }
    return words
        .split(' ')
        .map((word) => upperFirst(word))
        .join(' ');
}

/* eslint-disable no-bitwise */
/* eslint-disable no-mixed-operators */
/**
 * Generate unique ID
 *
 */
function uID(prefix = '') {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    const crypto = window.crypto || window['msCrypto'];
    const uuid = `${[1e7]}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, (c) => {
        const result = c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4;
        return result.toString(16);
    });
    return `${prefix}_${uuid}`;
}

class PermissionGuard {
    constructor(injector, router) {
        this.injector = injector;
        this.router = router;
    }
    doCheck(route) {
        const userService = this.injector.get(USER_SERVICE_TOKEN);
        return userService
            .profile$
            .pipe(take(1), map((profile) => {
            const { permitted_actions } = route.data;
            const isRoutePermitted = isPermitted(profile.permitted_actions, permitted_actions);
            if (!isRoutePermitted) {
                this.router.navigate([route.data.redirectTo]);
                return false;
            }
            return true;
        }));
    }
    canActivate(route) {
        return this.doCheck(route);
    }
    canLoad(route) {
        return this.doCheck(route);
    }
}
PermissionGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermissionGuard, deps: [{ token: i0.Injector }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
PermissionGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermissionGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermissionGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i1.Router }]; } });

class RouteExtrasStateGuard {
    constructor(injector, router) {
        this.injector = injector;
        this.router = router;
    }
    doCheck() {
        const { redirectTo } = this.injector.get(ROUTE_EXTRAS_STATE_GUARD_CONFIG);
        const { state } = this.router.getCurrentNavigation().extras;
        if (!state || !Object.keys(state).length) {
            this.router.navigate([redirectTo]);
            return false;
        }
        return true;
    }
    canActivate() {
        return this.doCheck();
    }
    canLoad() {
        return this.doCheck();
    }
}
RouteExtrasStateGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RouteExtrasStateGuard, deps: [{ token: i0.Injector }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
RouteExtrasStateGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RouteExtrasStateGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RouteExtrasStateGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i1.Router }]; } });

class BaseHttpService {
    constructor(http) {
        this.http = http;
    }
    get(url, options) {
        return this.http.get(url, options);
    }
    post(url, body, options) {
        return this.http.post(url, body, options);
    }
    patch(url, body, options) {
        return this.http.patch(url, body, options);
    }
    put(url, body, options) {
        return this.http.put(url, body, options);
    }
    delete(url, options) {
        return this.http.delete(url, options);
    }
    head(url, options) {
        return this.http.head(url, options);
    }
}
BaseHttpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BaseHttpService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
BaseHttpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BaseHttpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BaseHttpService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

const SENTRY_TAG = {
    HTTP_ERROR_RESPONSE: 'HttpErrorResponse',
    ERROR: 'Error',
};
const SENTRY_ERROR = {
    UNKNOWN_ERROR: 'Unknown Error',
    CLIENT_ERROR: 'Client Error',
    API_ERROR: 'API Error',
};
class SentinelErrorHandler {
    constructor(injector) {
        this.injector = injector;
        this.toastrConfig = {
            enableHtml: true,
            timeOut: 5000,
            closeButton: false,
        };
        this.options = { ...this.injector.get(SENTINEL_CONFIG) };
    }
    unwrapError(errorCandidate) {
        let error = errorCandidate;
        // Try to unwrap zone.js error.
        // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
        if (error && error.ngOriginalError) {
            error = error.ngOriginalError;
        }
        // Try to unwrap promise error.
        // https://github.com/angular/angular/issues/27840
        while (error.rejection) {
            error = error.rejection;
        }
        return error;
    }
    sentryReport(error, isException, tagTitle, tagMessage) {
        Sentry.withScope((scope) => {
            scope.setTag(tagTitle, tagMessage);
            if (!isException) {
                Sentry.captureMessage(error.message);
                return;
            }
            Sentry.captureException(error.originalError || error);
        });
    }
    getMessage(error) {
        if (!error?.error?.errors) {
            return error.message;
        }
        return formatApiErrors(error?.error?.errors).join(' ') || error.message;
    }
    notify(errorCandidate) {
        const toastr = this.injector.get(ToastrService);
        const ngZone = this.injector.get(NgZone);
        const error = this.unwrapError(errorCandidate);
        if (this.options.logErrors) {
            console.error(error);
        }
        ngZone.run(() => {
            if (error instanceof HttpErrorResponse) {
                if (!navigator.onLine) {
                    toastr.warning(WARNING.NO_NETWORK);
                    return;
                }
                if (!error.status) {
                    if (!this.options.environment.production) {
                        toastr.warning(error.message, error.statusText, this.toastrConfig);
                    }
                    else {
                        toastr.warning(ERROR.SOMETHING_WENT_WRONG);
                    }
                    return;
                }
                const message = this.getMessage(error);
                toastr.warning(message, error.statusText, this.toastrConfig);
            }
        });
    }
    processError(errorCandidate) {
        const error = this.unwrapError(errorCandidate);
        if (error instanceof HttpErrorResponse) {
            // Handle HttpClient Error
            if (!error.status) {
                this.sentryReport(error, false, SENTRY_TAG.HTTP_ERROR_RESPONSE, SENTRY_ERROR.UNKNOWN_ERROR);
                return;
            }
            this.handleErrorStatus(error.status);
            if (error.status === 422) {
                return;
            }
            this.sentryReport(error, false, SENTRY_TAG.HTTP_ERROR_RESPONSE, SENTRY_ERROR.API_ERROR);
            return;
        }
        // Handle Client Error (Angular Error, ReferenceError...)
        this.sentryReport(error, true, SENTRY_TAG.ERROR, SENTRY_ERROR.CLIENT_ERROR);
    }
    handleErrorStatus(status) {
        const handler = this.injector.get(GLOBAL_ERROR_STATUS_HANDLER_TOKEN, undefined, InjectFlags.Optional);
        if (!handler) {
            return;
        }
        handler.performActionFor(status);
    }
    handleError(error) {
        this.processError(error);
        this.notify(error);
    }
}
SentinelErrorHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SentinelErrorHandler, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
SentinelErrorHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SentinelErrorHandler, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SentinelErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class CollectionHelpersService {
    constructor(sentinelErrorHandler) {
        this.sentinelErrorHandler = sentinelErrorHandler;
    }
    /**
    * Inform global handler about error and throw it further
    *
    */
    retrowError() {
        return catchError((error) => {
            this.sentinelErrorHandler.handleError(error);
            return throwError(error);
        });
    }
    /**
    * Remap response to Collection response format
    *
    */
    mapToCollection(page_number) {
        return map((response) => {
            const rows = response.data;
            const page = {
                page_number: page_number - 1,
                per_page: response.pagination.per_page,
                total_objects: response.pagination.total_objects,
                total_pages: response.pagination.total_pages,
            };
            return { rows, page };
        });
    }
    /**
     * Change response filters format
     *
     */
    mapResponseFilters() {
        return map(({ filters, ...response }) => {
            if (!filters) {
                return response;
            }
            const collectionFilters = {};
            Object.keys(filters).forEach((filterKey) => {
                const filterCandidate = filters[filterKey];
                // Filters can be present as plain object
                if (!Array.isArray(filterCandidate)) {
                    collectionFilters[filterKey] = filterCandidate;
                    return;
                }
                // Filters can be present as array of objects
                collectionFilters[filterKey] = filterCandidate.map((item) => {
                    return {
                        id: uID(item.name),
                        ...item,
                        // TODO: remove 'selected' prop
                        selected: false,
                    };
                });
            });
            return {
                ...response,
                filters: collectionFilters,
            };
        });
    }
}
CollectionHelpersService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollectionHelpersService, deps: [{ token: SentinelErrorHandler }], target: i0.ɵɵFactoryTarget.Injectable });
CollectionHelpersService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollectionHelpersService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollectionHelpersService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SentinelErrorHandler }]; } });

/**
 * Customizable retry with increased duration
 *
 * @param param RetryStrategyParams
 */
function genericRetryStrategy({ maxRetryAttempts = 3, scalingDuration = 1000, excludedStatusCodes = [], } = {}) {
    return retryWhen((attempts) => attempts
        .pipe(mergeMap((error, i) => {
        const retryAttempt = i + 1;
        // if maximum number of retries have been met
        // or response is a status code we don't wish to retry, throw error
        if (retryAttempt > maxRetryAttempts || excludedStatusCodes.find((e) => e === error.status)) {
            return throwError(error);
        }
        // retry after 1s, 2s, etc...
        return timer(retryAttempt * scalingDuration);
    })));
}

function ofType(type) {
    return filter((_) => type === _.type);
}

/**
 * Set default image when property image not exist
 *
 */
function propertyImagePlaceholder(propertyLocked, imagePlaceholder) {
    return map((response) => {
        const setDefaultImage = (item) => {
            if (item.overlimit_trial_lead) {
                item.image = propertyLocked;
                return item;
            }
            if (!item.image) {
                item.image = imagePlaceholder;
            }
            return item;
        };
        return {
            ...response,
            data: response.data.map(setDefaultImage),
        };
    });
}

/* eslint-disable max-classes-per-file */
const ACTION_TYPE$2 = {
    LOAD: 'load',
    RELOAD: 'reload',
    LOAD_SUCCESS: 'load success',
    LOAD_MORE: 'load more',
    LOAD_MORE_SUCCESS: 'load more success',
    LOAD_FAILED: 'load failed',
    RESET: 'reset',
    SET_QUERY_PARAMS: 'set query params',
    CLEAR_QUERY_PARAMS: 'clear query params',
    SET_PER_PAGE_VALUE: 'set per page value',
    UPDATE_MULTISELECT: 'UPDATE_MULTISELECT',
    SET_MULTISELECT: 'SET_MULTISELECT',
    DESELECT_ALL_ITEMS: 'DESELECT_ALL_ITEMS',
    SELECT_ALL_ITEMS: 'SELECT_ALL_ITEMS',
    SET_CURRENT_PAGE_SELECTED_STATUS: 'SET_CURRENT_PAGE_SELECTED_STATUS',
    UPDATE_EXCLUDED: 'UPDATE_EXCLUDED',
    SET_EXCLUDED: 'SET_EXCLUDED',
};
/** @dynamic */
class CollectionStoreService {
    constructor(service) {
        this.service = service;
        /**
         * Defaults
         */
        this.defaultState = {
            data: [],
            pagination: null,
            filters: null,
            offset: 0,
            per_page: 0,
            loading: false,
            error: null,
            pathParams: null,
            queryParams: null,
            multiselect: null,
        };
        /**
         * Actions
         */
        this.actions$ = new BehaviorSubject({ type: null });
        /**
         * Effects
         */
        this.load$ = this.actions$.pipe(ofType(ACTION_TYPE$2.LOAD), exhaustMap((action) => this.perPage$.pipe(first(), map((per_page) => {
            const { offset, ...rest } = action.payload;
            return {
                page: `${parseInt(action.payload.offset, 10) + 1}`,
                per_page: `${per_page || 10}`,
                ...rest,
            };
        }))), exhaustMap(({ page, per_page, pathParams, ...rest }) => this.queryParams$.pipe(first(), map((currentQueryParams) => {
            return {
                pathParams,
                queryParams: {
                    page,
                    per_page,
                    ...rest,
                    ...currentQueryParams,
                },
            };
        }))), exhaustMap(({ queryParams, pathParams }) => this.pathParams$.pipe(first(), map((currentPathParams) => {
            return {
                queryParams,
                pathParams: {
                    ...pathParams,
                    ...currentPathParams,
                },
            };
        }))), exhaustMap((action) => this.multiselect$.pipe(first(), map((multiselectState) => {
            return {
                ...action,
                selectedItems: multiselectState?.selectedItems,
            };
        }))), switchMap(({ queryParams, pathParams, selectedItems }) => this
            .service
            .getCollection(queryParams, pathParams)
            .pipe(map((payload) => {
            const isPageSelected = this.checkIfPageItemsAreSelected(payload.data, selectedItems);
            return {
                payload: {
                    ...cloneDeep(payload),
                    isAllPageItemsSelected: isPageSelected,
                    offset: parseInt(queryParams.page, 10) - 1,
                },
                type: ACTION_TYPE$2.LOAD_SUCCESS,
            };
        }), catchError((error) => of({
            type: ACTION_TYPE$2.LOAD_FAILED,
            payload: error,
        })))));
        this.reload$ = this.actions$.pipe(ofType(ACTION_TYPE$2.RELOAD), exhaustMap(() => this.offset$.pipe(first(), map((page_number) => {
            return { page: `${page_number + 1}` };
        }))), exhaustMap((params) => this.perPage$.pipe(first(), map((per_page) => {
            return {
                ...params,
                per_page: `${per_page || 10}`,
            };
        }))), exhaustMap(({ page, per_page }) => this.queryParams$.pipe(first(), map((currentQueryParams) => {
            return {
                page,
                per_page,
                ...currentQueryParams,
            };
        }))), exhaustMap((queryParams) => this.pathParams$.pipe(first(), map((currentPathParams) => {
            return {
                queryParams,
                pathParams: currentPathParams,
            };
        }))), exhaustMap(({ queryParams, pathParams }) => this
            .service
            .getCollection(queryParams, pathParams)
            .pipe(map((payload) => {
            return {
                payload: {
                    ...cloneDeep(payload),
                    offset: parseInt(queryParams.page, 10) - 1,
                },
                type: ACTION_TYPE$2.LOAD_SUCCESS,
            };
        }), catchError((error) => of({
            type: ACTION_TYPE$2.LOAD_FAILED,
            payload: error,
        })))));
        this.loadMore$ = this.actions$.pipe(ofType(ACTION_TYPE$2.LOAD_MORE), exhaustMap((action) => this.offset$.pipe(first(), map((page_number) => {
            const params = action.payload ?? {};
            return {
                ...params,
                page: `${page_number + 2}`,
            };
        }))), exhaustMap((params) => this.perPage$.pipe(first(), map((per_page) => {
            return {
                ...params,
                per_page: `${per_page || 10}`,
            };
        }))), exhaustMap(({ page, per_page, ...rest }) => this.queryParams$.pipe(first(), map((currentQueryParams) => {
            return {
                page,
                per_page,
                ...rest,
                ...currentQueryParams,
            };
        }))), exhaustMap((queryParams) => this.pathParams$.pipe(first(), map((currentPathParams) => {
            return {
                queryParams,
                pathParams: currentPathParams,
            };
        }))), exhaustMap(({ queryParams, pathParams }) => this
            .service
            .getCollection(queryParams, pathParams)
            .pipe(map((payload) => {
            return {
                payload: {
                    ...cloneDeep(payload),
                    offset: parseInt(queryParams.page, 10) - 1,
                },
                type: ACTION_TYPE$2.LOAD_MORE_SUCCESS,
            };
        }), catchError((error) => of({
            type: ACTION_TYPE$2.LOAD_FAILED,
            payload: error,
        })))));
        this.setSelected$ = this.actions$.pipe(ofType(ACTION_TYPE$2.UPDATE_MULTISELECT), exhaustMap((action) => this.rows$.pipe(first(), map((rows) => {
            let isAllItemsOnPageSelected = false;
            if (action.payload.selected.length >= rows.length) {
                const pageItemsIds = rows.map((row) => row.id);
                const currStateItemsIds = action.payload.selected.map((item) => item.id);
                isAllItemsOnPageSelected = pageItemsIds.every((selectedId) => currStateItemsIds.includes(selectedId));
            }
            return {
                type: action.type,
                payload: {
                    ...action.payload,
                    isAllItemsOnPageSelected,
                },
            };
        }))), exhaustMap((action) => this.multiselect$.pipe(first(), map(() => {
            return {
                payload: action.payload,
                type: ACTION_TYPE$2.SET_MULTISELECT,
            };
        }))));
        this.setExcluded$ = this.actions$.pipe(ofType(ACTION_TYPE$2.UPDATE_EXCLUDED), exhaustMap((action) => this.rows$.pipe(first(), map((rows) => {
            return { action, rows };
        }))), exhaustMap(({ rows, action }) => this.multiselect$.pipe(first(), map((multiselect) => {
            const currentItem = action.payload.selected[0];
            const alreadyExcludedItems = multiselect.excludedItems;
            const removeFromExclude = !!alreadyExcludedItems.find((item) => item.id === currentItem.id);
            const excludedItems = removeFromExclude ? (alreadyExcludedItems.filter((item) => item.id !== currentItem.id)) : ([...alreadyExcludedItems, currentItem]);
            if (excludedItems.length >= rows.length) {
                return {
                    type: ACTION_TYPE$2.DESELECT_ALL_ITEMS,
                    payload: { excludedItems },
                };
            }
            return {
                type: ACTION_TYPE$2.SET_EXCLUDED,
                payload: { excludedItems },
            };
        }))));
        this.checkIfAllPageItemsAreSelected$ = this.actions$.pipe(ofType(ACTION_TYPE$2.SET_CURRENT_PAGE_SELECTED_STATUS), exhaustMap(() => this.rows$.pipe(first())), exhaustMap((currentPageItems) => this.multiselect$.pipe(first(), map((multiselectState) => {
            if (!multiselectState?.selectedItems) {
                return {
                    payload: false,
                    type: ACTION_TYPE$2.SET_CURRENT_PAGE_SELECTED_STATUS,
                };
            }
            let isAllItemsOnPageSelected = false;
            if (multiselectState.selectedItems.length >= currentPageItems.length) {
                const pageItemsIds = currentPageItems.map((row) => row.id);
                const currStateItemsIds = multiselectState.selectedItems.map((item) => item.id);
                isAllItemsOnPageSelected = pageItemsIds.every((selectedId) => currStateItemsIds.includes(selectedId));
            }
            return {
                payload: isAllItemsOnPageSelected,
                type: ACTION_TYPE$2.SET_CURRENT_PAGE_SELECTED_STATUS,
            };
        }))));
        /**
         * Dispatcher
         */
        this.dispatcher$ = merge(this.actions$, this.load$, this.reload$, this.loadMore$, this.setSelected$, this.checkIfAllPageItemsAreSelected$, this.setExcluded$);
        /**
         * State Reducer
         */
        this.state$ = this.dispatcher$.pipe(startWith(this.defaultState), scan(this.reducer), shareReplay({
            bufferSize: 1,
            refCount: false,
        }));
        /**
         * Selectors
         */
        this.rows$ = this.state$.pipe(map((state) => state.data));
        this.offset$ = this.state$.pipe(map((state) => state.offset));
        this.perPage$ = this.state$.pipe(map((state) => state.per_page));
        this.loading$ = this.state$.pipe(map((state) => state.loading));
        this.pagination$ = this.state$.pipe(map((state) => state.pagination));
        this.filters$ = this
            .state$
            .pipe(map((state) => state.filters));
        this.error$ = this.state$.pipe(map((state) => state.error));
        this.pathParams$ = this.state$.pipe(map((state) => state.pathParams));
        this.queryParams$ = this.state$.pipe(map((state) => state.queryParams));
        this.multiselect$ = this.state$.pipe(map((state) => state.multiselect));
    }
    checkIfPageItemsAreSelected(items, selectedItems) {
        if (!selectedItems || items.length > selectedItems.length) {
            return false;
        }
        const pageItemsIds = items.map((row) => row.id);
        const currStateItemsIds = selectedItems.map((item) => item.id);
        return pageItemsIds.every((selectedId) => currStateItemsIds.includes(selectedId));
    }
    ngOnDestroy() {
        this.actions$.complete();
    }
    dispatch(action) {
        this.actions$.next(action);
    }
    reducer(state, action) {
        switch (action.type) {
            case ACTION_TYPE$2.LOAD: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                    pathParams: {
                        ...state.pathParams,
                        ...action.payload.pathParams,
                    },
                };
            }
            case ACTION_TYPE$2.RELOAD: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                };
            }
            case ACTION_TYPE$2.LOAD_MORE: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                };
            }
            case ACTION_TYPE$2.LOAD_SUCCESS: {
                const isAllItemsOnPageSelected = action.payload.isAllPageItemsSelected;
                const multiselectState = isAllItemsOnPageSelected === undefined ? ({ ...state.multiselect }) : ({ ...state.multiselect, isAllItemsOnPageSelected });
                return {
                    ...state,
                    data: [
                        ...action.payload.data,
                    ],
                    multiselect: multiselectState,
                    pagination: { ...action.payload.pagination },
                    offset: action.payload.offset,
                    filters: action.payload.filters ? { ...action.payload.filters } : null,
                    loading: false,
                };
            }
            case ACTION_TYPE$2.LOAD_MORE_SUCCESS: {
                return {
                    ...state,
                    data: [
                        ...state.data,
                        ...action.payload.data,
                    ],
                    pagination: { ...action.payload.pagination },
                    offset: action.payload.offset,
                    filters: action.payload.filters ? { ...action.payload.filters } : null,
                    loading: false,
                };
            }
            case ACTION_TYPE$2.SET_QUERY_PARAMS: {
                const params = {
                    ...state.queryParams,
                    ...action.payload,
                };
                const result = {};
                Object.keys(params).forEach((key) => {
                    if (params[key] !== ''
                        && params[key] !== null
                        && params[key] !== undefined
                        && params[key] !== false) {
                        result[key] = params[key];
                    }
                });
                return {
                    ...state,
                    queryParams: result,
                    pathParams: {
                        ...state.pathParams,
                        ...action.payload.pathParams,
                    },
                };
            }
            case ACTION_TYPE$2.CLEAR_QUERY_PARAMS: {
                return {
                    ...state,
                    queryParams: {},
                };
            }
            case ACTION_TYPE$2.SET_PER_PAGE_VALUE: {
                return {
                    ...state,
                    per_page: action.payload.per_page,
                };
            }
            case ACTION_TYPE$2.LOAD_FAILED:
                return {
                    ...state,
                    offset: -1,
                    error: action.payload,
                    loading: false,
                };
            case ACTION_TYPE$2.RESET: {
                return this.defaultState;
            }
            case ACTION_TYPE$2.SET_MULTISELECT: {
                const newMultiselectState = {
                    ...cloneDeep(state.multiselect),
                    selectedItems: action.payload.selected,
                    isAllItemsOnPageSelected: action.payload.isAllItemsOnPageSelected,
                };
                return {
                    ...cloneDeep(state),
                    multiselect: newMultiselectState,
                };
            }
            case ACTION_TYPE$2.SELECT_ALL_ITEMS: {
                return {
                    ...state,
                    multiselect: {
                        ...state.multiselect,
                        isAllItemsSelected: true,
                        excludedItems: [],
                        selectedItems: [],
                    },
                };
            }
            case ACTION_TYPE$2.DESELECT_ALL_ITEMS: {
                return {
                    ...state,
                    multiselect: {
                        selectedItems: [],
                        isAllItemsOnPageSelected: false,
                        isAllItemsSelected: false,
                        excludedItems: [],
                    },
                };
            }
            case ACTION_TYPE$2.SET_CURRENT_PAGE_SELECTED_STATUS: {
                return {
                    ...state,
                    multiselect: {
                        ...state.multiselect,
                        isAllItemsOnPageSelected: action.payload,
                    },
                };
            }
            case ACTION_TYPE$2.SET_EXCLUDED: {
                return {
                    ...state,
                    multiselect: {
                        selectedItems: [],
                        isAllItemsSelected: true,
                        isAllItemsOnPageSelected: false,
                        excludedItems: action.payload.excludedItems,
                    },
                };
            }
            default: {
                return state;
            }
        }
    }
    /**
     * Actions
     */
    load(offset, per_page = 10, params) {
        asapScheduler.schedule(() => this.dispatch({
            payload: { per_page },
            type: ACTION_TYPE$2.SET_PER_PAGE_VALUE,
        }));
        asapScheduler.schedule(() => this.dispatch({
            payload: { offset, ...params },
            type: ACTION_TYPE$2.LOAD,
        }));
    }
    reload() {
        this.dispatch({ type: ACTION_TYPE$2.RELOAD });
    }
    loadMore(params) {
        if (!params) {
            this.dispatch({ type: ACTION_TYPE$2.LOAD_MORE });
            return;
        }
        this.dispatch({
            payload: { ...params },
            type: ACTION_TYPE$2.LOAD_MORE,
        });
    }
    emitFilters(payload) {
        asapScheduler.schedule(() => this.dispatch({
            payload,
            type: ACTION_TYPE$2.SET_QUERY_PARAMS,
        }));
        asapScheduler.schedule(() => this.dispatch({
            payload: { offset: 0 },
            type: ACTION_TYPE$2.LOAD,
        }));
    }
    clearFilters(dispatchLoadAction = true) {
        asapScheduler.schedule(() => this.dispatch({ type: ACTION_TYPE$2.CLEAR_QUERY_PARAMS }));
        if (dispatchLoadAction) {
            asapScheduler.schedule(() => this.dispatch({
                payload: { offset: 0 },
                type: ACTION_TYPE$2.LOAD,
            }));
        }
    }
    setSelected(selected) {
        this.dispatch({
            payload: { selected },
            type: ACTION_TYPE$2.UPDATE_MULTISELECT,
        });
    }
    setExcluded(selected) {
        this.dispatch({
            payload: { selected },
            type: ACTION_TYPE$2.UPDATE_EXCLUDED,
        });
    }
    selectAllItems() {
        this.dispatch({ type: ACTION_TYPE$2.SELECT_ALL_ITEMS });
    }
    deselectAllItems() {
        this.dispatch({ type: ACTION_TYPE$2.DESELECT_ALL_ITEMS });
    }
    checkIfAllPageItemsAreSelected() {
        this.dispatch({ type: ACTION_TYPE$2.SET_CURRENT_PAGE_SELECTED_STATUS });
    }
}
CollectionStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollectionStoreService, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive });
CollectionStoreService.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: CollectionStoreService, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollectionStoreService, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: undefined }]; } });

class DocumentService {
    constructor(window, document) {
        this.window = window;
        this.document = document;
        this.style = this.buildStyleElement();
    }
    assign(param) {
        this.document.location.assign(param);
    }
    reload() {
        this.document.location.reload();
    }
    appendOverflowBody() {
        this.document.body.appendChild(this.style);
    }
    removeOverflowBody() {
        if (this.window.getComputedStyle(this.document.body, null).getPropertyValue('overflow') === 'hidden') {
            this.document.body.removeChild(this.style);
        }
    }
    buildStyleElement() {
        const bodyStyle = this.document.createElement('style');
        bodyStyle.setAttribute('type', 'text/css');
        bodyStyle.textContent = 'body { overflow: hidden !important; }';
        return bodyStyle;
    }
}
DocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DocumentService, deps: [{ token: WINDOW }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
DocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DocumentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

const ACTION_TYPE$1 = {
    LOAD: 'load',
    LOAD_SUCCESS: 'load success',
    LOAD_FAILED: 'load failed',
    RESET: 'reset',
    SET_QUERY_PARAMS: 'set query params',
    CLEAR_QUERY_PARAMS: 'clear query params',
};
/** @dynamic */
class EntityStoreService {
    constructor(service) {
        this.service = service;
        /**
         * Defaults
         */
        this.defaultState = {
            data: null,
            filters: null,
            loading: false,
            error: null,
            queryParams: null,
            pathParams: null,
        };
        /**
         * Actions
         */
        this.actions$ = new BehaviorSubject({ type: null });
        /**
         * Effects
         */
        this.load$ = this.actions$.pipe(ofType(ACTION_TYPE$1.LOAD), exhaustMap(() => this.queryParams$.pipe(first(), map((currentQueryParams) => {
            return { ...currentQueryParams };
        }))), exhaustMap((queryParams) => this.pathParams$.pipe(first(), map((currentPathParams) => {
            return {
                queryParams,
                pathParams: currentPathParams,
            };
        }))), exhaustMap(({ queryParams, pathParams }) => this
            .service
            .getEntity(queryParams, pathParams)
            .pipe(map((payload) => {
            return {
                payload: {
                    ...cloneDeep(payload),
                    offset: parseInt(queryParams.page, 10) - 1,
                },
                type: ACTION_TYPE$1.LOAD_SUCCESS,
            };
        }), catchError((error) => of({
            type: ACTION_TYPE$1.LOAD_FAILED,
            payload: error,
        })))));
        /**
         * Dispatcher
         */
        this.dispatcher$ = merge(this.actions$, this.load$);
        /**
         * State Reducer
         */
        this.state$ = this.dispatcher$.pipe(startWith(this.defaultState), scan(this.reducer), shareReplay({
            bufferSize: 1,
            refCount: false,
        }));
        /**
         * Selectors
         */
        this.data$ = this.state$.pipe(map((state) => state.data));
        this.loading$ = this.state$.pipe(map((state) => state.loading));
        this.filters$ = this.state$.pipe(map((state) => state.filters));
        this.error$ = this.state$.pipe(map((state) => state.error));
        this.queryParams$ = this.state$.pipe(map((state) => state.queryParams));
        this.pathParams$ = this.state$.pipe(map((state) => state.pathParams));
    }
    ngOnDestroy() {
        this.actions$.complete();
    }
    dispatch(action) {
        this.actions$.next(action);
    }
    reducer(state, action) {
        switch (action.type) {
            case ACTION_TYPE$1.LOAD: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                    pathParams: {
                        ...state.pathParams,
                        ...action.payload,
                    },
                };
            }
            case ACTION_TYPE$1.LOAD_SUCCESS: {
                return {
                    ...state,
                    data: { ...action.payload.data },
                    filters: action.payload.filters ? { ...action.payload.filters } : null,
                    loading: false,
                };
            }
            case ACTION_TYPE$1.SET_QUERY_PARAMS: {
                const params = {
                    ...state.queryParams,
                    ...action.payload,
                };
                const result = {};
                Object.keys(params).forEach((key) => {
                    if (params[key] !== ''
                        && params[key] !== null
                        && params[key] !== undefined
                        && params[key] !== false) {
                        result[key] = params[key];
                    }
                });
                return {
                    ...state,
                    queryParams: result,
                };
            }
            case ACTION_TYPE$1.CLEAR_QUERY_PARAMS: {
                return {
                    ...state,
                    queryParams: {},
                };
            }
            case ACTION_TYPE$1.LOAD_FAILED:
                return {
                    ...state,
                    error: action.payload,
                    loading: false,
                };
            case ACTION_TYPE$1.RESET: {
                return this.defaultState;
            }
            default: {
                return state;
            }
        }
    }
    /**
     * Actions
     */
    load(params) {
        this.dispatch({
            payload: params,
            type: ACTION_TYPE$1.LOAD,
        });
    }
    emitFilters(payload) {
        asapScheduler.schedule(() => this.dispatch({
            payload,
            type: ACTION_TYPE$1.SET_QUERY_PARAMS,
        }));
        asapScheduler.schedule(() => this.dispatch({ type: ACTION_TYPE$1.LOAD }));
    }
    clearFilters(dispatchLoadAction = true) {
        asapScheduler.schedule(() => this.dispatch({ type: ACTION_TYPE$1.CLEAR_QUERY_PARAMS }));
        if (dispatchLoadAction) {
            asapScheduler.schedule(() => this.dispatch({ type: ACTION_TYPE$1.LOAD }));
        }
    }
}
EntityStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EntityStoreService, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive });
EntityStoreService.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: EntityStoreService, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EntityStoreService, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: undefined }]; } });

class FileReaderService {
    readAsDataURL(file) {
        return new Observable((subscriber) => {
            if (!(file instanceof Blob)) {
                subscriber.error(new Error('`file` must be an instance of File or Blob.'));
                return subscriber;
            }
            const fileReader = new FileReader();
            fileReader.onerror = (error) => subscriber.error(error);
            fileReader.onabort = (error) => subscriber.error(error);
            fileReader.onload = () => subscriber.next(fileReader.result);
            fileReader.onloadend = () => subscriber.complete();
            fileReader.readAsDataURL(file);
            // eslint-disable-next-line consistent-return
            return subscriber;
        });
    }
    createObjectURL(file, element) {
        return new Observable((subscriber) => {
            const url = URL.createObjectURL(file);
            element.setAttribute('src', url);
            // eslint-disable-next-line no-param-reassign
            element.onload = () => {
                subscriber.next(true);
                subscriber.complete();
                URL.revokeObjectURL(url);
            };
            return subscriber;
        });
    }
}
FileReaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileReaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FileReaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileReaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileReaderService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class IntersectionObserverService {
    create(options) {
        const onCreate = (handler) => {
            const observer = new IntersectionObserver(handler, {
                root: options.root,
                threshold: options.threshold || 0,
                rootMargin: options.rootMargin || '0px 0px 0px 0px',
            });
            observer.observe(options.target);
            return observer;
        };
        const onDestroy = (handler, token) => {
            if (!token) {
                return;
            }
            token.disconnect();
        };
        return fromEventPattern(onCreate, onDestroy).pipe(map((result) => result[0][0]), filter((entry) => {
            const result = options.isOnlyIntersecting ? entry.isIntersecting : true;
            return result;
        }));
    }
}
IntersectionObserverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: IntersectionObserverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
IntersectionObserverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: IntersectionObserverService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: IntersectionObserverService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class ModalService {
    constructor(simpleModal) {
        this.simpleModal = simpleModal;
    }
    open(content, data, options) {
        const modal = this.simpleModal.addModal(content, data, options);
        return modal;
    }
    removeAll() {
        return this.simpleModal.removeAll();
    }
}
ModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalService, deps: [{ token: i1$2.SimpleModalService }], target: i0.ɵɵFactoryTarget.Injectable });
ModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.SimpleModalService }]; } });

class ResponseErrorHandler {
    markControlWithApiError(controlName, form) {
        const control = form.get(controlName);
        if (control) {
            control.markAsDirty();
            control.setErrors({ api_error: true });
        }
    }
    setErrorMessage(controlName, errorsModel, message) {
        const controlRules = errorsModel[controlName.split('.').reverse()[0]];
        const errorRule = find(controlRules, { name: 'api_error' });
        if (!errorRule) {
            console.warn(`No error rule assigned to [${controlName}] control`);
            errorsModel.global.push(`${controlName}: ${message}`);
            return;
        }
        errorRule.text = message;
    }
    process(response, form, errorsModel) {
        response?.error?.errors?.forEach((error) => {
            const pointer = error.source.pointer || get(error, 'detail.source.pointer', null);
            const message = typeof error.detail === 'string' ? error.detail : error?.detail?.detail;
            const isGlobalPointer = !pointer || pointer === '/data/attributes/base';
            const isNewMessage = !~errorsModel.global.indexOf(message);
            if (isGlobalPointer) {
                if (isNewMessage) {
                    errorsModel.global.push(message);
                }
                return;
            }
            const controlName = pointer.split('/').reverse()[0];
            this.markControlWithApiError(controlName, form);
            this.setErrorMessage(controlName, errorsModel, message);
        });
    }
    processWithoutControls(response) {
        if (!response) {
            return [];
        }
        return formatApiErrors(response);
    }
    format(response) {
        if (!response) {
            return '';
        }
        return formatApiErrors(response).join('<br>');
    }
}
ResponseErrorHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ResponseErrorHandler, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ResponseErrorHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ResponseErrorHandler, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ResponseErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class Sentinel {
    constructor(environment) {
        this.environment = environment;
    }
    onguard() {
        if (!this.environment.sentryDSN) {
            return;
        }
        Sentry.init({
            dsn: this.environment.sentryDSN,
            maxBreadcrumbs: 8,
            environment: this.environment.name,
            // TryCatch has to be configured to disable XMLHttpRequest wrapping, as we are going to handle
            // http module exceptions manually in Angular's ErrorHandler and we don't want it to capture the same error twice.
            // Please note that TryCatch configuration requires at least @sentry/browser v5.16.0.
            integrations: [
                new Sentry.Integrations.TryCatch({ XMLHttpRequest: false }),
            ],
            ignoreErrors: [
                'Non-Error exception captured',
                'Non-Error promise rejection captured',
            ],
            whitelistUrls: [
                ...this.environment.sentryWhitelist,
            ],
        });
    }
}

// Consts
const ACTION_TYPE = {
    LOAD: 'load',
    RELOAD: 'reload',
    LOAD_SUCCESS: 'load success',
    LOAD_MORE: 'load more',
    LOAD_MORE_SUCCESS: 'load more success',
    LOAD_FAILED: 'load failed',
    SEASON_END: 'season end',
    RESET: 'reset',
    SET_QUERY_PARAMS: 'set query params',
    CLEAR_QUERY_PARAMS: 'clear query params',
    SET_PER_PAGE_VALUE: 'set per page value',
};
/** @dynamic */
class SeriesStoreService {
    constructor(service) {
        this.service = service;
        /**
         * Defaults
         */
        this.defaultState = {
            data: null,
            filters: null,
            offset: 0,
            per_page: 0,
            seasonEnd: false,
            loading: false,
            error: null,
            queryParams: null,
        };
        /**
         * Actions
         */
        this.actions$ = new BehaviorSubject({ type: null });
        /**
         * Effects
         */
        this.load$ = this.actions$.pipe(ofType(ACTION_TYPE.LOAD), exhaustMap((action) => this.offset$.pipe(first(), map(() => {
            return { page: `${parseInt(action.payload.offset, 10) + 1}` };
        }))), exhaustMap((params) => this.applyPerPage(params)), exhaustMap((params) => this.applyQueryParams(params)), switchMap((params) => this.callSeriesService(params, ACTION_TYPE.LOAD_SUCCESS, ACTION_TYPE.LOAD_FAILED)));
        this.reload$ = this.actions$.pipe(ofType(ACTION_TYPE.RELOAD), exhaustMap(() => this.offset$.pipe(first(), map((page_number) => {
            return { page: `${page_number + 1}` };
        }))), exhaustMap((params) => this.applyPerPage(params)), exhaustMap((params) => this.applyQueryParams(params)), exhaustMap((params) => this.callSeriesService(params, ACTION_TYPE.LOAD_SUCCESS, ACTION_TYPE.LOAD_FAILED)));
        this.loadMore$ = this.actions$.pipe(ofType(ACTION_TYPE.LOAD_MORE), exhaustMap(() => this.offset$.pipe(first(), map((page_number) => {
            return { page: `${(page_number || 0) + 2}` };
        }))), exhaustMap((params) => this.applyPerPage(params)), exhaustMap((params) => this.applyQueryParams(params)), exhaustMap((params) => this.callSeriesService(params, ACTION_TYPE.LOAD_MORE_SUCCESS, ACTION_TYPE.LOAD_FAILED)));
        /**
         * Dispatcher
         */
        this.dispatcher$ = merge(this.actions$, this.load$, this.reload$, this.loadMore$);
        /**
         * State Reducer
         */
        this.state$ = this.dispatcher$.pipe(startWith(this.defaultState), scan(this.reducer), shareReplay({
            bufferSize: 1,
            refCount: false,
        }));
        /**
         * Selectors
         */
        this.data$ = this.state$.pipe(map((state) => state.data));
        this.offset$ = this.state$.pipe(map((state) => state.offset));
        this.perPage$ = this.state$.pipe(map((state) => state.per_page));
        this.loading$ = this.state$.pipe(map((state) => state.loading));
        this.seasonEnd$ = this.state$.pipe(map((state) => state.seasonEnd));
        this.filters$ = this
            .state$
            .pipe(map((state) => state.filters));
        this.error$ = this.state$.pipe(map((state) => state.error));
        this.queryParams$ = this.state$.pipe(map((state) => state.queryParams));
    }
    ngOnDestroy() {
        this.actions$.complete();
    }
    /**
     * Actions
     */
    load(offset, per_page = 10) {
        asapScheduler.schedule(() => this.dispatch({
            payload: { per_page },
            type: ACTION_TYPE.SET_PER_PAGE_VALUE,
        }));
        asapScheduler.schedule(() => this.dispatch({
            payload: { offset },
            type: ACTION_TYPE.LOAD,
        }));
    }
    reload() {
        this.dispatch({ type: ACTION_TYPE.RELOAD });
    }
    loadMore() {
        this.dispatch({ type: ACTION_TYPE.LOAD_MORE });
    }
    emitFilters(payload) {
        asapScheduler.schedule(() => this.dispatch({
            payload,
            type: ACTION_TYPE.SET_QUERY_PARAMS,
        }));
        asapScheduler.schedule(() => this.dispatch({
            payload: { offset: 0 },
            type: ACTION_TYPE.LOAD,
        }));
    }
    clearFilters(dispatchLoadAction = true) {
        asapScheduler.schedule(() => this.dispatch({ type: ACTION_TYPE.CLEAR_QUERY_PARAMS }));
        if (dispatchLoadAction) {
            asapScheduler.schedule(() => this.dispatch({
                payload: { offset: 0 },
                type: ACTION_TYPE.LOAD,
            }));
        }
    }
    reset() {
        asapScheduler.schedule(() => this.dispatch({ type: ACTION_TYPE.RESET }));
    }
    dispatch(action) {
        this.actions$.next(action);
    }
    reducer(state, action) {
        switch (action.type) {
            case ACTION_TYPE.LOAD: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                };
            }
            case ACTION_TYPE.RELOAD: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                    seasonEnd: false,
                };
            }
            case ACTION_TYPE.LOAD_MORE: {
                return {
                    ...state,
                    error: null,
                    loading: true,
                };
            }
            case ACTION_TYPE.LOAD_SUCCESS: {
                return {
                    ...state,
                    data: [
                        ...action.payload.data,
                    ],
                    offset: action.payload.offset,
                    filters: action.payload.filters ? { ...action.payload.filters } : null,
                    loading: false,
                };
            }
            case ACTION_TYPE.LOAD_MORE_SUCCESS: {
                return {
                    ...state,
                    data: [
                        ...(state?.data || []),
                        ...action.payload.data,
                    ],
                    offset: action.payload.offset,
                    filters: action.payload.filters ? { ...action.payload.filters } : null,
                    loading: false,
                };
            }
            case ACTION_TYPE.SEASON_END: {
                return {
                    ...state,
                    data: [
                        ...(state?.data || []),
                        ...action.payload.data,
                    ],
                    offset: 0,
                    filters: action.payload.filters ? { ...action.payload.filters } : null,
                    loading: false,
                    seasonEnd: true,
                };
            }
            case ACTION_TYPE.SET_QUERY_PARAMS: {
                const params = {
                    ...state.queryParams,
                    ...action.payload,
                };
                const result = {};
                Object.keys(params).forEach((key) => {
                    if (params[key] !== ''
                        && params[key] !== null
                        && params[key] !== undefined
                        && params[key] !== false) {
                        result[key] = params[key];
                    }
                });
                return {
                    ...state,
                    data: [],
                    offset: state.offset - 1,
                    seasonEnd: false,
                    queryParams: result,
                };
            }
            case ACTION_TYPE.CLEAR_QUERY_PARAMS: {
                return {
                    ...state,
                    data: [],
                    seasonEnd: false,
                    queryParams: {},
                };
            }
            case ACTION_TYPE.SET_PER_PAGE_VALUE: {
                return {
                    ...state,
                    per_page: action.payload.per_page,
                };
            }
            case ACTION_TYPE.LOAD_FAILED:
                return {
                    ...state,
                    offset: -1,
                    error: action.payload,
                    loading: false,
                };
            case ACTION_TYPE.RESET: {
                return { ...this.defaultState };
            }
            default: {
                return state;
            }
        }
    }
    applyPerPage(params) {
        return this.perPage$.pipe(first(), map((per_page) => {
            return {
                ...params,
                per_page: `${per_page || 10}`,
            };
        }));
    }
    applyQueryParams({ page, per_page }) {
        return this.queryParams$.pipe(first(), map((currentQueryParams) => {
            return {
                page,
                per_page,
                ...currentQueryParams,
            };
        }));
    }
    callSeriesService(params, successActionType, errorActionType) {
        return this
            .service
            .getSeries(params)
            .pipe(map((payload) => {
            if (!payload.data?.length || (payload.data?.length < +params.per_page)) {
                return {
                    payload: {
                        ...cloneDeep(payload),
                        offset: parseInt(params.page, 10) - 1,
                    },
                    type: ACTION_TYPE.SEASON_END,
                };
            }
            return {
                payload: {
                    ...cloneDeep(payload),
                    offset: parseInt(params.page, 10) - 1,
                },
                type: successActionType,
            };
        }), catchError((error) => of({
            type: errorActionType,
            payload: error,
        })));
    }
}
SeriesStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeriesStoreService, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive });
SeriesStoreService.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: SeriesStoreService, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeriesStoreService, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: undefined }]; } });

class ThemeService {
    constructor(document, rendererFactory2) {
        this.document = document;
        this.rendererFactory2 = rendererFactory2;
        this.renderer2 = this.rendererFactory2.createRenderer(null, null);
    }
    setTheme(name) {
        if (!name) {
            return;
        }
        this.renderer2.addClass(this.document.body, `${name}-theme`);
    }
}
ThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ThemeService, deps: [{ token: DOCUMENT }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.RendererFactory2 }]; } });

class VaultService {
    constructor(window) {
        this.window = window;
    }
    store(type) {
        return type === 'local' ? this.window.localStorage : this.window.sessionStorage;
    }
    set(key, value, type = 'local') {
        if (!key || !value) {
            return;
        }
        this.store(type).setItem(key, JSON.stringify(value));
    }
    get(key, type = 'local') {
        let data = this.store(type).getItem(key);
        try {
            data = JSON.parse(data);
        }
        catch (error) {
            console.warn(error);
        }
        if (!data) {
            return null;
        }
        return data;
    }
    remove(key, type = 'local') {
        if (!this.store(type).getItem(key)) {
            return;
        }
        this.store(type).removeItem(key);
    }
    empty(type = 'local') {
        this.store(type).clear();
    }
}
VaultService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: VaultService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
VaultService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: VaultService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: VaultService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class APIInterceptor {
    constructor(injector) {
        this.injector = injector;
        this.config = { ...this.injector.get(API_INTECEPTOR_CONFIG) };
    }
    intercept(httpRequest, httpHandler) {
        const shouldBypass = this.config.bypass.some((url) => httpRequest.url.includes(url));
        if (!shouldBypass) {
            return httpHandler.handle(httpRequest.clone({ url: `${this.config.api}/${httpRequest.url}` }));
        }
        return httpHandler.handle(httpRequest);
    }
}
APIInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: APIInterceptor, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
APIInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: APIInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: APIInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class AuthTokenInterceptor {
    constructor(injector, vaultService) {
        this.injector = injector;
        this.vaultService = vaultService;
        this.config = { ...this.injector.get(AUTH_TOKEN_CONFIG) };
    }
    intercept(httpRequest, httpHandler) {
        const shouldBypass = this.config.bypass.some((url) => httpRequest.url.includes(url));
        if (!shouldBypass) {
            const token = this.vaultService.get(this.config.jwt);
            return httpHandler
                .handle(httpRequest.clone({
                setHeaders: {
                    ...!!token && { jwt: token },
                    ...(httpRequest.method === 'POST') && { 'ngsw-bypass': 'true' },
                },
            }));
        }
        return httpHandler.handle(httpRequest);
    }
}
AuthTokenInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthTokenInterceptor, deps: [{ token: i0.Injector }, { token: VaultService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthTokenInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthTokenInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthTokenInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: VaultService }]; } });

class JSONApiDeserializerInterceptor {
    constructor() {
        this.jsona = new Jsona();
    }
    deserialize(response) {
        if (Object.prototype.hasOwnProperty.call(response, 'body')
            && !!response.body
            && Object.prototype.hasOwnProperty.call(response.body, 'meta')
            && !Object.prototype.hasOwnProperty.call(response.body, 'data')) {
            return response.body.meta;
        }
        const { body } = response;
        const data = this.jsona.deserialize(body);
        if (Object.prototype.hasOwnProperty.call(body, 'meta')) {
            return { ...body?.meta, data };
        }
        return data;
    }
    processResponse(event) {
        if (!(event instanceof HttpResponse)) {
            return event;
        }
        return event.clone({ body: event.body ? this.deserialize(event) : event.body });
    }
    intercept(httpRequest, httpHandler) {
        return httpHandler
            .handle(httpRequest)
            .pipe(map(this.processResponse.bind(this)));
    }
}
JSONApiDeserializerInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: JSONApiDeserializerInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
JSONApiDeserializerInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: JSONApiDeserializerInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: JSONApiDeserializerInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class ClickOutsideDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.clickOutside = new EventEmitter();
    }
    onDocumentClick(event) {
        const targetElement = event.target;
        if (targetElement && !this.elementRef.nativeElement.contains(targetElement)) {
            this.clickOutside.emit(event);
        }
    }
}
ClickOutsideDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClickOutsideDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ClickOutsideDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: ClickOutsideDirective, selector: "[clickOutside]", outputs: { clickOutside: "clickOutside" }, host: { listeners: { "document:click": "onDocumentClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[clickOutside]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { clickOutside: [{
                type: Output
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class CountdownDirective {
    constructor(element) {
        this.element = element;
        this.start = 10;
        this.countdown = new EventEmitter();
    }
    ngAfterViewInit() {
        this.element.nativeElement.textContent = this.start;
        timer(1000, 1000)
            .pipe(map((item) => this.start - item), take(this.start + 1))
            .subscribe({
            next: (value) => {
                this.element.nativeElement.textContent = value;
            },
            complete: () => {
                this.countdown.emit();
            },
        });
    }
}
CountdownDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CountdownDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
CountdownDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: CountdownDirective, selector: "[countdown]", inputs: { start: "start" }, outputs: { countdown: "countdown" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CountdownDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[countdown]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { start: [{
                type: Input
            }], countdown: [{
                type: Output
            }] } });

let CounterAnimationDirective = class CounterAnimationDirective {
    constructor(animationFrame$, performance, currencyPipe, elementRef, intersectionObserverService, ngZone) {
        this.animationFrame$ = animationFrame$;
        this.performance = performance;
        this.currencyPipe = currencyPipe;
        this.elementRef = elementRef;
        this.intersectionObserverService = intersectionObserverService;
        this.ngZone = ngZone;
        this.counterAnimation = 0;
        this.duration = 500;
        this.isAnimating = true;
        this.isIntersecting = false;
    }
    ngOnChanges(changes) {
        if (!changes?.counterAnimation.firstChange) {
            this.isAnimating = true;
            if (this.isIntersecting) {
                this.isAnimating = false;
                this.animation$.unsubscribe();
                this.initCounter(changes?.counterAnimation.previousValue, changes?.counterAnimation.currentValue);
            }
        }
    }
    ngOnInit() {
        this.ngZone.runOutsideAngular(() => {
            this
                .intersectionObserverService
                .create({ target: this.elementRef.nativeElement })
                .pipe(untilDestroyed(this))
                .subscribe((entry) => {
                this.isIntersecting = entry.isIntersecting;
                if (this.isIntersecting && this.isAnimating) {
                    this.initCounter(0, this.counterAnimation);
                    this.isAnimating = false;
                }
            });
        });
    }
    initCounter(previousValue, currentValue) {
        if (!currentValue) {
            return;
        }
        const start = this.performance.now();
        this.animation$ = this
            .animationFrame$
            .pipe(throttleTime(1000 / 30), // 30 fps
        untilDestroyed(this))
            .subscribe((time) => {
            const progress = Math.min(((time - start) / this.duration), 1);
            let result = 0;
            if (previousValue < currentValue) {
                result = Math.floor(previousValue + ((currentValue - previousValue) * progress));
            }
            else {
                result = Math.floor(currentValue + ((previousValue - currentValue) * (1 - progress)));
            }
            this.renderValue(result);
            if (!((time - start) <= this.duration)) {
                this.animation$.unsubscribe();
            }
        });
    }
    renderValue(value) {
        const element = this.elementRef.nativeElement;
        element.innerText = this.currencyPipe.transform(value, 'USD', 'symbol-narrow', '1.0-0');
    }
};
CounterAnimationDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CounterAnimationDirective, deps: [{ token: ANIMATION_FRAME }, { token: PERFORMANCE }, { token: i1$3.CurrencyPipe }, { token: i0.ElementRef }, { token: IntersectionObserverService }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
CounterAnimationDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: CounterAnimationDirective, selector: "[counterAnimation]", inputs: { counterAnimation: "counterAnimation", duration: "duration" }, usesOnChanges: true, ngImport: i0 });
CounterAnimationDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Observable, Object, CurrencyPipe,
        ElementRef,
        IntersectionObserverService,
        NgZone])
], CounterAnimationDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CounterAnimationDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[counterAnimation]' }]
        }], ctorParameters: function () { return [{ type: i3.Observable, decorators: [{
                    type: Inject,
                    args: [ANIMATION_FRAME]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PERFORMANCE]
                }] }, { type: i1$3.CurrencyPipe }, { type: i0.ElementRef }, { type: IntersectionObserverService }, { type: i0.NgZone }]; }, propDecorators: { counterAnimation: [{
                type: Input
            }], duration: [{
                type: Input
            }] } });

class ExternalLinkDirective {
    constructor() {
        this.rel = 'noopener';
        this.target = '_blank';
    }
}
ExternalLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
ExternalLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: ExternalLinkDirective, selector: "a:not([routerLink])[href]", inputs: { rel: "rel", target: "target" }, host: { properties: { "attr.rel": "this.rel", "attr.target": "this.target" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalLinkDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'a:not([routerLink])[href]' }]
        }], propDecorators: { rel: [{
                type: HostBinding,
                args: ['attr.rel']
            }, {
                type: Input
            }], target: [{
                type: HostBinding,
                args: ['attr.target']
            }, {
                type: Input
            }] } });

class LazyImageDirective {
    constructor(platformId, element, renderer) {
        this.platformId = platformId;
        this.element = element;
        this.renderer = renderer;
        this.currentElement = this.element.nativeElement;
        this.fallbackImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    }
    get opacity() { return '0'; }
    get transition() { return 'opacity 0.27s ease-in 0.27s'; }
    ngOnChanges(changes) {
        const { src } = changes;
        if (!src) {
            return;
        }
        this.renderer.setStyle(this.currentElement, 'opacity', 0);
        this.transform(src.currentValue);
    }
    ngAfterViewInit() {
        this.transform(this.src);
    }
    transform(src) {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        if (!src || src === '') {
            this.loadImage(this.fallbackImage);
            return;
        }
        this.loadImage(src);
    }
    loadImage(imageUrl) {
        this.currentElement.src = this.fallbackImage;
        const img = new Image();
        if (imageUrl) {
            img.src = imageUrl;
        }
        else {
            img.src = this.fallbackImage;
        }
        img.onload = () => {
            this.currentElement.src = img.src;
            this.updateStyleAfterLoad();
        };
        img.onerror = () => {
            this.currentElement.src = this.fallbackImage;
            this.updateStyleAfterLoad();
        };
    }
    updateStyleAfterLoad() {
        this.renderer.setStyle(this.currentElement, 'opacity', 1);
    }
}
LazyImageDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LazyImageDirective, deps: [{ token: PLATFORM_ID }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
LazyImageDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: LazyImageDirective, selector: "[lazy-image]", inputs: { src: "src" }, host: { properties: { "style.opacity": "this.opacity", "style.transition": "this.transition" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LazyImageDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[lazy-image]' }]
        }], ctorParameters: function () { return [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { src: [{
                type: Input
            }], opacity: [{
                type: HostBinding,
                args: ['style.opacity']
            }], transition: [{
                type: HostBinding,
                args: ['style.transition']
            }] } });

class NgxErrorsDirective {
    constructor(form) {
        this.form = form;
        this.ready = false;
    }
    get errors() {
        if (!this.ready) {
            return null;
        }
        return this.control.errors;
    }
    get hasErrors() {
        return !!this.errors;
    }
    ngOnInit() {
        this.subject = new BehaviorSubject(null);
    }
    ngOnChanges() {
        this.control = this.form.control.get(this.ngxErrorsControl);
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.checkStatus();
            this.control.statusChanges.subscribe(this.checkStatus.bind(this));
        });
    }
    ngOnDestroy() {
        if (this.subject) {
            this.subject.complete();
        }
    }
    hasError(name, conditions) {
        return this.checkPropState('invalid', name, conditions);
    }
    isValid(name, conditions) {
        return this.checkPropState('valid', name, conditions);
    }
    getError(name) {
        if (!this.ready) {
            return null;
        }
        return this.control.getError(name);
    }
    toArray(value) {
        return Array.isArray(value) ? value : [value];
    }
    checkPropState(prop, name, conditions) {
        if (!this.ready) {
            return false;
        }
        const controlPropsState = (!conditions || (Array.isArray(conditions) ? conditions : [conditions]).every((condition) => this.control[condition]));
        if (name.charAt(0) === '*') {
            return this.control[prop] && controlPropsState;
        }
        return (prop === 'valid' ? !this.control.hasError(name) : this.control.hasError(name) && controlPropsState);
    }
    checkStatus() {
        const { errors } = this.control;
        this.ready = true;
        if (!errors) {
            return;
        }
        Object.entries(errors).forEach((error) => {
            this.subject.next({ control: this.control, errorName: error[0] });
        });
    }
}
NgxErrorsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgxErrorsDirective, deps: [{ token: i1$4.FormGroupDirective }], target: i0.ɵɵFactoryTarget.Directive });
NgxErrorsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: NgxErrorsDirective, selector: "[ngxErrors]", inputs: { ngxErrorsControl: ["ngxErrors", "ngxErrorsControl"] }, exportAs: ["ngxErrors"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgxErrorsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxErrors]',
                    exportAs: 'ngxErrors',
                }]
        }], ctorParameters: function () { return [{ type: i1$4.FormGroupDirective }]; }, propDecorators: { ngxErrorsControl: [{
                type: Input,
                args: ['ngxErrors']
            }] } });

let NgxErrorDirective = class NgxErrorDirective {
    constructor(ngxErrors) {
        this.ngxErrors = ngxErrors;
        this.hidden = true;
        this.rules = [];
        this.errorNames = [];
    }
    set ngxError(value) {
        this.errorNames = Array.isArray(value) ? value : [value];
    }
    set when(value) {
        this.rules = Array.isArray(value) ? value : [value];
    }
    ngOnInit() {
        this.states = new Subject();
        this.states$ = this.states.asObservable().pipe(distinctUntilChanged());
        const errors = this
            .ngxErrors
            .subject
            .pipe(filter(Boolean), filter((obj) => !!~this.errorNames.indexOf(obj.errorName)));
        const states$ = this
            .states$
            .pipe(map((state) => this.rules.every((rule) => !!~state.indexOf(rule))));
        this.subscription = combineLatest([states$, errors])
            .pipe(untilDestroyed(this))
            .subscribe(([state, error]) => {
            this.hidden = !(state && error.control.hasError(error.errorName));
        });
    }
    ngDoCheck() {
        this.states.next(this.rules.filter((rule) => this.ngxErrors.control[rule]));
    }
    ngOnDestroy() {
        this.states.complete();
        this.subscription.unsubscribe();
    }
};
NgxErrorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgxErrorDirective, deps: [{ token: forwardRef(() => NgxErrorsDirective) }], target: i0.ɵɵFactoryTarget.Directive });
NgxErrorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: NgxErrorDirective, selector: "[ngxError]", inputs: { ngxError: "ngxError", when: "when" }, host: { properties: { "hidden": "this.hidden" } }, ngImport: i0 });
NgxErrorDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [NgxErrorsDirective])
], NgxErrorDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgxErrorDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[ngxError]' }]
        }], ctorParameters: function () { return [{ type: NgxErrorsDirective, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => NgxErrorsDirective)]
                }] }]; }, propDecorators: { ngxError: [{
                type: Input
            }], when: [{
                type: Input
            }], hidden: [{
                type: HostBinding,
                args: ['hidden']
            }] } });

let OverflowShadowDirective = class OverflowShadowDirective {
    constructor(window, document, renderer, elementRef, intersectionObserverService) {
        this.window = window;
        this.document = document;
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.intersectionObserverService = intersectionObserverService;
    }
    ngOnInit() {
        this.setScreenHeightCustomProperty();
        this.subscribes();
    }
    ngAfterViewInit() {
        const { nativeElement } = this.elementRef;
        if (!('IntersectionObserver' in this.window)) {
            return;
        }
        this.createBottomPlaceholderDiv(nativeElement);
        this.createBottomShadowDiv(nativeElement);
    }
    subscribes() {
        fromEvent(this.window, 'resize')
            .pipe(untilDestroyed(this))
            .subscribe((event) => {
            this.setScreenHeightCustomProperty(event.target.innerHeight);
        });
    }
    createBottomPlaceholderDiv(nativeElement) {
        const bottomPlaceholderDiv = this.renderer.createElement('div');
        this.renderer.appendChild(nativeElement, bottomPlaceholderDiv);
        this
            .intersectionObserverService
            .create({
            root: nativeElement,
            target: bottomPlaceholderDiv,
            isOnlyIntersecting: false,
        })
            .pipe(untilDestroyed(this))
            .subscribe((entry) => {
            if (entry.isIntersecting) {
                this.renderer.removeClass(this.bottomShadow, 'show');
            }
            else {
                this.renderer.addClass(this.bottomShadow, 'show');
            }
        });
    }
    createBottomShadowDiv(nativeElement) {
        this.bottomShadow = this.renderer.createElement('div');
        this.renderer.addClass(this.bottomShadow, 'scroll-shadow');
        this.renderer.appendChild(nativeElement, this.bottomShadow);
    }
    setScreenHeightCustomProperty(height) {
        const vh = (height || this.window.innerHeight) * 0.01;
        this.document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
};
OverflowShadowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OverflowShadowDirective, deps: [{ token: WINDOW }, { token: DOCUMENT }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: IntersectionObserverService }], target: i0.ɵɵFactoryTarget.Directive });
OverflowShadowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: OverflowShadowDirective, selector: "[overflowShadow]", inputs: { overflowShadow: "overflowShadow" }, ngImport: i0 });
OverflowShadowDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Object, Object, Renderer2,
        ElementRef,
        IntersectionObserverService])
], OverflowShadowDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OverflowShadowDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[overflowShadow]' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: IntersectionObserverService }]; }, propDecorators: { overflowShadow: [{
                type: Input
            }] } });

let PermittedIfAllDirective = class PermittedIfAllDirective {
    constructor(injector, templateRef, viewContainer) {
        this.injector = injector;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.permissions = [];
        this.isHidden = true;
    }
    set permittedIfAll(permissions) {
        this.permissions = permissions;
        this.updateView();
    }
    ngOnInit() {
        const userService = this.injector.get(USER_SERVICE_TOKEN);
        userService
            .profile$
            .pipe(untilDestroyed(this))
            .subscribe((profile) => {
            this.profile = profile;
            this.updateView();
        });
    }
    updateView() {
        if (this.checkPermission()) {
            if (this.isHidden) {
                this.viewContainer.createEmbeddedView(this.templateRef);
                this.isHidden = false;
            }
        }
        else {
            this.isHidden = true;
            this.viewContainer.clear();
        }
    }
    checkPermission() {
        let permitted = false;
        if (!this.permissions?.length) {
            return false;
        }
        if (this.profile?.permitted_actions) {
            permitted = this.permissions.every((permission) => this.profile.permitted_actions.includes(permission));
        }
        return permitted;
    }
};
PermittedIfAllDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermittedIfAllDirective, deps: [{ token: i0.Injector }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
PermittedIfAllDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: PermittedIfAllDirective, selector: "[permittedIfAll]", inputs: { permittedIfAll: "permittedIfAll" }, ngImport: i0 });
PermittedIfAllDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Injector,
        TemplateRef,
        ViewContainerRef])
], PermittedIfAllDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermittedIfAllDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[permittedIfAll]' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { permittedIfAll: [{
                type: Input
            }] } });

let PermittedIfAnyDirective = class PermittedIfAnyDirective {
    constructor(injector, templateRef, viewContainer) {
        this.injector = injector;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.permissions = [];
        this.isHidden = true;
    }
    set permittedIfAny(permissions) {
        this.permissions = permissions;
        this.updateView();
    }
    ngOnInit() {
        const userService = this.injector.get(USER_SERVICE_TOKEN);
        userService
            .profile$
            .pipe(untilDestroyed(this))
            .subscribe((profile) => {
            this.profile = profile;
            this.updateView();
        });
    }
    updateView() {
        if (this.checkPermission()) {
            if (this.isHidden) {
                this.viewContainer.createEmbeddedView(this.templateRef);
                this.isHidden = false;
            }
        }
        else {
            this.isHidden = true;
            this.viewContainer.clear();
        }
    }
    checkPermission() {
        let permitted = false;
        if (!this.permissions?.length) {
            return false;
        }
        if (this.profile?.permitted_actions) {
            permitted = this.permissions.some((permission) => this.profile.permitted_actions.includes(permission));
        }
        return permitted;
    }
};
PermittedIfAnyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermittedIfAnyDirective, deps: [{ token: i0.Injector }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
PermittedIfAnyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: PermittedIfAnyDirective, selector: "[permittedIfAny]", inputs: { permittedIfAny: "permittedIfAny" }, ngImport: i0 });
PermittedIfAnyDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Injector,
        TemplateRef,
        ViewContainerRef])
], PermittedIfAnyDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermittedIfAnyDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[permittedIfAny]' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { permittedIfAny: [{
                type: Input
            }] } });

let PermittedIfDirective = class PermittedIfDirective {
    constructor(injector, templateRef, viewContainer) {
        this.injector = injector;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.elseTemplateRef = null;
        this.elseViewRef = null;
        this.permission = null;
    }
    /**
     * The rule to evaluate as the condition for showing a template.
     */
    set permittedIf(permission) {
        this.permission = permission;
        this.updateView();
    }
    /**
     * A template to show if the permission is not present in profile.
     */
    set permittedIfElse(templateRef) {
        assertTemplate('permittedIfElse', templateRef);
        this.elseTemplateRef = templateRef;
        this.elseViewRef = null; // clear previous view if any.
        this.updateView();
    }
    ngOnInit() {
        const userService = this.injector.get(USER_SERVICE_TOKEN);
        userService
            .profile$
            .pipe(untilDestroyed(this))
            .subscribe((profile) => {
            this.profile = profile;
            this.updateView();
        });
    }
    updateView() {
        if (this.checkPermission()) {
            this.viewContainer.clear();
            this.elseViewRef = null;
            this.viewContainer.createEmbeddedView(this.templateRef);
            return;
        }
        if (!this.elseViewRef) {
            this.viewContainer.clear();
            if (this.elseTemplateRef) {
                this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef);
            }
        }
    }
    checkPermission() {
        if (!this.permission) {
            return true;
        }
        if (this.profile?.permitted_actions) {
            return this.profile.permitted_actions.includes(this.permission);
        }
        return true;
    }
};
PermittedIfDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermittedIfDirective, deps: [{ token: i0.Injector }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
PermittedIfDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: PermittedIfDirective, selector: "[permittedIf]", inputs: { permittedIf: "permittedIf", permittedIfElse: "permittedIfElse" }, ngImport: i0 });
PermittedIfDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Injector,
        TemplateRef,
        ViewContainerRef])
], PermittedIfDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PermittedIfDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[permittedIf]' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { permittedIf: [{
                type: Input
            }], permittedIfElse: [{
                type: Input
            }] } });

let RoleIfAnyDirective = class RoleIfAnyDirective {
    constructor(injector, templateRef, viewContainer) {
        this.injector = injector;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.elseTemplateRef = null;
        this.elseViewRef = null;
        this.roles = [];
    }
    /**
     * The rule to evaluate as the condition for showing a template.
     */
    set roleIfAny(roles) {
        this.roles = roles;
        this.updateView();
    }
    /**
     * A template to show if the permission is not present in profile.
     */
    set roleIfAnyElse(templateRef) {
        assertTemplate('roleIfElse', templateRef);
        this.elseTemplateRef = templateRef;
        this.elseViewRef = null; // clear previous view if any.
        this.updateView();
    }
    ngOnInit() {
        const userService = this.injector.get(USER_SERVICE_TOKEN);
        userService
            .profile$
            .pipe(untilDestroyed(this))
            .subscribe((profile) => {
            this.profile = profile;
            this.updateView();
        });
    }
    updateView() {
        if (this.checkPermission()) {
            this.viewContainer.clear();
            this.elseViewRef = null;
            this.viewContainer.createEmbeddedView(this.templateRef);
            return;
        }
        if (!this.elseViewRef) {
            this.viewContainer.clear();
            if (this.elseTemplateRef) {
                this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef);
            }
        }
    }
    checkPermission() {
        let permitted = false;
        if (!this.roles?.length) {
            return false;
        }
        if (this.profile?.role) {
            permitted = this.roles.some((role) => this.profile.role === role);
        }
        return permitted;
    }
};
RoleIfAnyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RoleIfAnyDirective, deps: [{ token: i0.Injector }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
RoleIfAnyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: RoleIfAnyDirective, selector: "[roleIfAny]", inputs: { roleIfAny: "roleIfAny", roleIfAnyElse: "roleIfAnyElse" }, ngImport: i0 });
RoleIfAnyDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Injector,
        TemplateRef,
        ViewContainerRef])
], RoleIfAnyDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RoleIfAnyDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[roleIfAny]' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { roleIfAny: [{
                type: Input
            }], roleIfAnyElse: [{
                type: Input
            }] } });

let RoleIfDirective = class RoleIfDirective {
    constructor(injector, templateRef, viewContainer) {
        this.injector = injector;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.elseTemplateRef = null;
        this.elseViewRef = null;
        this.role = null;
    }
    /**
     * The rule to evaluate as the condition for showing a template.
     */
    set roleIf(role) {
        this.role = role;
        this.updateView();
    }
    /**
     * A template to show if the permission is not present in profile.
     */
    set roleIfElse(templateRef) {
        assertTemplate('roleIfElse', templateRef);
        this.elseTemplateRef = templateRef;
        this.elseViewRef = null; // clear previous view if any.
        this.updateView();
    }
    ngOnInit() {
        const userService = this.injector.get(USER_SERVICE_TOKEN);
        userService
            .profile$
            .pipe(untilDestroyed(this))
            .subscribe((profile) => {
            this.profile = profile;
            this.updateView();
        });
    }
    updateView() {
        if (this.checkPermission()) {
            this.viewContainer.clear();
            this.elseViewRef = null;
            this.viewContainer.createEmbeddedView(this.templateRef);
            return;
        }
        if (!this.elseViewRef) {
            this.viewContainer.clear();
            if (this.elseTemplateRef) {
                this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef);
            }
        }
    }
    checkPermission() {
        let permitted = false;
        if (!this.role) {
            return false;
        }
        if (this.profile?.role) {
            permitted = this.profile.role === this.role;
        }
        return permitted;
    }
};
RoleIfDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RoleIfDirective, deps: [{ token: i0.Injector }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
RoleIfDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: RoleIfDirective, selector: "[roleIf]", inputs: { roleIf: "roleIf", roleIfElse: "roleIfElse" }, ngImport: i0 });
RoleIfDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Injector,
        TemplateRef,
        ViewContainerRef])
], RoleIfDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RoleIfDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[roleIf]' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { roleIf: [{
                type: Input
            }], roleIfElse: [{
                type: Input
            }] } });

const directives = [
    ClickOutsideDirective,
    CountdownDirective,
    CounterAnimationDirective,
    ExternalLinkDirective,
    LazyImageDirective,
    NgxErrorDirective,
    NgxErrorsDirective,
    OverflowShadowDirective,
    PermittedIfAllDirective,
    PermittedIfAnyDirective,
    PermittedIfDirective,
    RoleIfAnyDirective,
    RoleIfDirective,
];
class AsapDirectivesModule {
}
AsapDirectivesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapDirectivesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AsapDirectivesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AsapDirectivesModule, declarations: [ClickOutsideDirective,
        CountdownDirective,
        CounterAnimationDirective,
        ExternalLinkDirective,
        LazyImageDirective,
        NgxErrorDirective,
        NgxErrorsDirective,
        OverflowShadowDirective,
        PermittedIfAllDirective,
        PermittedIfAnyDirective,
        PermittedIfDirective,
        RoleIfAnyDirective,
        RoleIfDirective], exports: [ClickOutsideDirective,
        CountdownDirective,
        CounterAnimationDirective,
        ExternalLinkDirective,
        LazyImageDirective,
        NgxErrorDirective,
        NgxErrorsDirective,
        OverflowShadowDirective,
        PermittedIfAllDirective,
        PermittedIfAnyDirective,
        PermittedIfDirective,
        RoleIfAnyDirective,
        RoleIfDirective] });
AsapDirectivesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapDirectivesModule, providers: [CurrencyPipe] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapDirectivesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: directives,
                    declarations: directives,
                    providers: [CurrencyPipe],
                }]
        }] });

class AmountPipe {
    constructor(currencyPipe) {
        this.currencyPipe = currencyPipe;
    }
    transform(value, currencyFormatOption) {
        if (!value) {
            return '$0';
        }
        const amount = +value;
        const format = currencyFormatOption || '1.0-0';
        return this.currencyPipe.transform(amount, 'USD', 'symbol-narrow', format);
    }
}
AmountPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AmountPipe, deps: [{ token: i1$3.CurrencyPipe }], target: i0.ɵɵFactoryTarget.Pipe });
AmountPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AmountPipe, name: "amount" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AmountPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'amount' }]
        }], ctorParameters: function () { return [{ type: i1$3.CurrencyPipe }]; } });

class FormatNumberPipe {
    transform(amount, fraction, excludeNegative) {
        const suffixes = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
        let exp;
        let fractionSize;
        let nagative;
        let truncated = amount;
        if (!amount) {
            return '0';
        }
        exp = 0;
        nagative = 0;
        if (!fraction) {
            fractionSize = 0;
        }
        else {
            fractionSize = fraction;
        }
        if (amount >= 1000 || amount < -1000) {
            if (amount < -1000) {
                nagative = true;
            }
            truncated = Math.abs(truncated);
            exp = Math.floor(Math.log(truncated) / Math.log(1000));
            truncated /= 1000 ** exp;
            truncated = nagative ? -truncated : truncated;
        }
        if (excludeNegative) {
            truncated = Math.abs(truncated);
        }
        truncated = +truncated.toFixed(fractionSize);
        const suffix = suffixes[exp];
        return `$${truncated}${suffix}`;
    }
}
FormatNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FormatNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FormatNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: FormatNumberPipe, name: "formatNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FormatNumberPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'formatNumber' }]
        }] });

class HumanizePipe {
    transform(value, symbolStub) {
        if (!value) {
            return symbolStub || '';
        }
        const date = momentMini(value);
        const duration = momentMini.duration(momentMini(date).diff(momentMini()));
        // If more than one week
        if (Math.trunc(duration.asWeeks())) {
            return date.format('MMM D');
        }
        return this.humanizeDuration(duration);
    }
    humanizeDuration(duration) {
        const humanizedDate = momentMini.duration(duration.asSeconds(), 'seconds').humanize(true);
        const result = humanizedDate.toLowerCase() === 'a few seconds ago' ? 'Just now' : humanizedDate;
        return result.charAt(0).toUpperCase() + result.slice(1);
    }
}
HumanizePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HumanizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HumanizePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: HumanizePipe, name: "humanize" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HumanizePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'humanize' }]
        }] });

/*
 * Converts newlines into html breaks
*/
class NewlinePipe {
    transform(value) {
        if (value.length) {
            return value.replace(/(?:\r\n|\r|\n)/g, '<br />');
        }
        return '';
    }
}
NewlinePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NewlinePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
NewlinePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NewlinePipe, name: "newline" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NewlinePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'newline' }]
        }] });

/*
 * Emits only non-zero values
*/
class NoZeroPipe {
    transform(value) {
        return value === '0' ? '' : value;
    }
}
NoZeroPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoZeroPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
NoZeroPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NoZeroPipe, name: "nozero" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoZeroPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'nozero' }]
        }] });

class OrdinalPipe {
    transform(int) {
        const ones = +int % 10;
        const tens = (+int % 100) - ones;
        return `${int}${['th', 'st', 'nd', 'rd'][tens === 10 || ones > 3 ? 0 : ones]}`;
    }
}
OrdinalPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrdinalPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
OrdinalPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: OrdinalPipe, name: "ordinal" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrdinalPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'ordinal' }]
        }] });

class PhonePipe {
    transform(value) {
        if (!value) {
            return '';
        }
        const parseNumber = parsePhoneNumberFromString(value);
        const phoneNumber = parseNumber ? parseNumber.formatNational() : value;
        return new AsYouType('US').input(phoneNumber);
    }
}
PhonePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PhonePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
PhonePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: PhonePipe, name: "phone" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PhonePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'phone' }]
        }] });

class SafeHtmlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(html) {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }
}
SafeHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SafeHtmlPipe, deps: [{ token: i1$5.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
SafeHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SafeHtmlPipe, name: "safeHtml" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SafeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safeHtml' }]
        }], ctorParameters: function () { return [{ type: i1$5.DomSanitizer }]; } });

const pipes = [
    AmountPipe,
    HumanizePipe,
    NewlinePipe,
    NoZeroPipe,
    OrdinalPipe,
    PhonePipe,
    SafeHtmlPipe,
    FormatNumberPipe,
];
class AsapPipesModule {
}
AsapPipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AsapPipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AsapPipesModule, declarations: [AmountPipe,
        HumanizePipe,
        NewlinePipe,
        NoZeroPipe,
        OrdinalPipe,
        PhonePipe,
        SafeHtmlPipe,
        FormatNumberPipe], exports: [AmountPipe,
        HumanizePipe,
        NewlinePipe,
        NoZeroPipe,
        OrdinalPipe,
        PhonePipe,
        SafeHtmlPipe,
        FormatNumberPipe] });
AsapPipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapPipesModule, providers: pipes });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: pipes,
                    declarations: pipes,
                    providers: pipes,
                }]
        }] });

// Validators
// Services
// Modules
const dialogs = [];
const shared = [];
const imports = [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AsapDirectivesModule,
    AsapPipesModule,
];
const exported = [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AsapDirectivesModule,
    AsapPipesModule,
];
const declarations = [];
class AsapCommonModule {
}
AsapCommonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AsapCommonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AsapCommonModule, imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AsapDirectivesModule,
        AsapPipesModule], exports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AsapDirectivesModule,
        AsapPipesModule] });
AsapCommonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapCommonModule, imports: [imports, CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AsapDirectivesModule,
        AsapPipesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AsapCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports,
                    exports: [
                        ...exported,
                        ...dialogs,
                        ...shared,
                    ],
                    declarations: [
                        ...declarations,
                        ...dialogs,
                        ...shared,
                    ],
                }]
        }] });

class RouteExtrasStateResolve {
    constructor(router) {
        this.router = router;
    }
    resolve() {
        return this.router.getCurrentNavigation()?.extras?.state;
    }
}
RouteExtrasStateResolve.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RouteExtrasStateResolve, deps: [{ token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
RouteExtrasStateResolve.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RouteExtrasStateResolve, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RouteExtrasStateResolve, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Router }]; } });

class FeatureFlagServiceConfig {
}

class FeatureFlagsService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.flagNames = null;
        if (config) {
            this.loadFlagNames();
        }
    }
    loadFlagNames() {
        return this.http.get(this.config.flagsSourceUrl)
            .pipe(tap((response) => { this.flagNames = response; }))
            .toPromise();
    }
    isFeatureEnabled(flag) {
        if (this.flagNames && has(this.flagNames, flag)) {
            return this.flagNames[flag]?.enabled_on[this.config.environmentName];
        }
        return false;
    }
}
FeatureFlagsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FeatureFlagsService, deps: [{ token: i1$1.HttpClient }, { token: FeatureFlagServiceConfig, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FeatureFlagsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FeatureFlagsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FeatureFlagsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: FeatureFlagServiceConfig, decorators: [{
                    type: Optional
                }] }]; } });

class CollectionApi {
}

class Store {
}

class CollectionStore extends Store {
}

class EntityApi {
}

class EntityStore extends Store {
}

class SeriesApi {
}

class SeriesStore extends Store {
}

function addressValidator(control) {
    if (!control.value) {
        return control.errors;
    }
    const errors = {};
    const isAllEmpty = [
        'city',
        'state',
        'street',
        'zip',
        'country',
    ]
        .map((name) => {
        if (!control.value[name]) {
            errors[name] = true;
            return true;
        }
        return false;
    })
        .every((item) => !!item);
    if (isAllEmpty) {
        return { incorrect: true };
    }
    if (Object.keys(errors).length) {
        return errors;
    }
    return null;
}

function phoneValidator(control) {
    const isValid = control.value && isValidNumber(control.value, 'US');
    return !control.value || isValid ? null : { invalidPhone: true };
}

function fileSizeValidator(config) {
    return (control) => {
        const { maxSize } = config;
        if (!maxSize) {
            return null;
        }
        return maxSize < control.value?.size ? { fileSize: true } : null;
    };
}

/*
 * Public API Surface of asap-tools
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APIInterceptor, API_DATE_FORMAT, API_INTECEPTOR_CONFIG, AUTH_TOKEN_CONFIG, AmountPipe, AsapCommonModule, AsapDirectivesModule, AsapPipesModule, AuthTokenInterceptor, BATH_PATTERN, BaseHttpService, COMMON_TOAST, ClickOutsideDirective, CollectionApi, CollectionHelpersService, CollectionStore, CollectionStoreService, CountdownDirective, CounterAnimationDirective, DEFAULT_MOMENT_DATE_FORMAT, DocumentService, EMAIL_PATTERN, ERROR, EntityApi, EntityStore, EntityStoreService, ExitGuard, ExternalLinkDirective, FACEBOOK_CAMP_ID, FACEBOOK_TRACKING_ID_PATTERN, FACEBOOK_USERNAME_PATTERN, FeatureFlagServiceConfig, FeatureFlagsService, FileReaderService, FormatNumberPipe, GLOBAL_ERROR_STATUS_HANDLER_TOKEN, GOOGLE_ADWORDS_PATTERN, GOOGLE_ANALYTICS_TRACKING_ID_PATTERN, GOOGLE_TAG_MANAGER_PATTERN, HumanizePipe, IntersectionObserverService, JSONApiDeserializerInterceptor, LENDER_MARGIN_PATTERN, LENDING_PAD_ID, LazyImageDirective, ModalService, NewlinePipe, NgxErrorDirective, NgxErrorsDirective, NoZeroPipe, OrdinalPipe, OverflowShadowDirective, PermissionGuard, PermittedIfAllDirective, PermittedIfAnyDirective, PermittedIfDirective, PhonePipe, ROUTE_EXTRAS_STATE_GUARD_CONFIG, ResponseErrorHandler, RoleIfAnyDirective, RoleIfDirective, RouteExtrasStateGuard, RouteExtrasStateResolve, SENTINEL_CONFIG, SafeHtmlPipe, Sentinel, SentinelErrorHandler, SeriesApi, SeriesStore, SeriesStoreService, Store, TRACKING_PROVIDER, ThemeService, USER_SERVICE_TOKEN, VaultService, WARNING, WORD_WITH_SPECIAL_CHARS_GLOBAL, addressValidator, assertTemplate, b64toBlob, chain, fadeIn, fadeInOut, fadeOut, fileSizeValidator, formatAddressWithUnit, formatApiErrors, formatEmailShortCodes, formatFees, genericRetryStrategy, getFileName, getInitials, isPermitted, isUrlIncludes, lodashToTitle, ofType, percent, phoneValidator, propertyImagePlaceholder, provideRefs, remapCommissions, remapGooglePlacesAddress, round, roundQuarter, stringToNumber, toDashCase, toFormData, toQueryParams, toTitleCase, uID, upperFirst };
